import { SimpleGrid, useToast } from "@chakra-ui/react";
import { useState } from "react";

import { UserDto } from "doxecure-commons/doxecureClientTypes";
import { WaitingRequestAction } from "doxecure-commons/doxecureTypes";
import { $ok } from "foundation-ts/commons";
import { Nullable } from "foundation-ts/types";

import { InvoicesCard } from "@components/SubcriptionCards/InvoicesCard";
import { MandateCard }  from "@components/SubcriptionCards/MandateCard";
import { PlanCard }     from "@components/SubcriptionCards/PlanCard";
import { UsersCard }    from "@components/SubcriptionCards/UsersCard";
import { Plan, Mandate } from "@components/SubcriptionCards/types";
import { config } from "@/config/config";
import { useMollie } from "@/contexts/MollieContext";
import { useSubscription } from "@hooks/useSubscription";
import { createCBMandate, updateMandate } from "@services/users.service";
import { defaultErrorToast, defaultSuccessToast } from "@/utils/toast";
import { SeaShellBox } from "@/components/SeaShellBox";

interface SubscriptionTabProps {
    connectedUser?: Nullable<UserDto>;
    pricePerExtraUser?: number;
}

export const SubscriptionTab = (props: SubscriptionTabProps) => {
    const { connectedUser, pricePerExtraUser } = props;
    const { data: activeSubscription, requestCancelSubscription, cancelTerminationRequest, startSubscriptionImmediately } = useSubscription();
    const { mollie, ready } = useMollie();
    const toast = useToast();
    const [mandate, setMandate] = useState<Nullable<Mandate>>(connectedUser?.corporations?.first()?.mandate as Mandate);

    const corporation           = connectedUser?.corporations?.first();
    const isTrialPeriod         = $ok(corporation?.trialPeriod?.end);
    const waitingRequestAction  = activeSubscription?.requestWaintingActionInfos?.type as WaitingRequestAction;
    const maxUsers              = isTrialPeriod ? corporation?.trialPeriod?.extraUsers ?? 1 : corporation?.maxUsers ?? -1;

    const handleCardSubmit = async (cbToken: string): Promise<void> => {
        const checkoutURL = await createCBMandate(corporation!.apid, cbToken);
        if ($ok(checkoutURL)) {
            window.location.href = checkoutURL!;
        }
    };
    
    const handleMandateSubmit = async (consumerAccount: string, consumerName: string): Promise<void> => {
        const corpDTO = await updateMandate(corporation!.apid, consumerName, consumerAccount);
        if ($ok(corpDTO)) { 
            setMandate(corpDTO.mandate);
            toast(defaultSuccessToast("Nous avons pris en compte votre nouveau mandat SEPA"));
        }
    };

    function handleChangePlan(plan: Plan): void {
        // setSub((s) => ({ ...s, plan, isTrial: false }));
    }

    const handleStartNow = async () => {
        try {
            await startSubscriptionImmediately.mutateAsync();
            if ($ok(corporation?.mandate?.cardNumber)) {

                toast(defaultSuccessToast(`Votre abonnement a bien démarré. Vous avez un crédit de ${activeSubscription?.signatureCount.subscriptionMax} signatures`));
            } else {
                toast(defaultSuccessToast("Nous avons bien pris en compte votre demande de démarrer plus tôt votre abonnement"));
            }
        } catch (e) {
            toast(defaultErrorToast("Une erreur est survenue lors de l'activation de votre abonnement"));
        }
    }

    const handleTerminate = async () => {
        try {
            await requestCancelSubscription.mutateAsync();
            toast(defaultSuccessToast("La demande de résiliation a bien été prise en compte"));
        } catch (e) {
            toast(defaultErrorToast("Une erreur est survenue lors de la résiliation de votre abonnement"));
        }
    }

    const handleCancelTerminationRequest = async() => {
        try {
            await cancelTerminationRequest.mutateAsync();
            toast(defaultSuccessToast("L'annulation de la résiliation a bien été prise en compte"));
        } catch (e) {
            toast(defaultErrorToast("Une erreur est survenue lors de la demande d'annulation de la résiliation"));
        }
    }

    function handleAddUsers(count: number): void {
        // setSub((s) => ({ ...s, usersAuthorized: s.usersAuthorized + count }));
    }

    function handleRemoveUsers(count: number): void {
//         setSub((s) => ({
//             ...s,
//             usersAuthorized: Math.max(s.plan.usersIncluded, s.usersAuthorized - count),
//         }));
    }

    return (
        <SeaShellBox component="form" px={4} py={10}>
            <SimpleGrid columns={{ base: 1, lg: 2}} spacing={5} mb={5}>
                <PlanCard
                    connectedUser={connectedUser}
                    subscription={activeSubscription}
                    waitingRequestAction={waitingRequestAction}
                    extraUsers={0}
                    pricePerExtraUser={pricePerExtraUser ?? 0}
                    onChangePlan={handleChangePlan}
                    onStartNow={handleStartNow}
                    onTerminate={handleTerminate}
                    onCancelTerminationRequest={handleCancelTerminationRequest}
                />
                <UsersCard
                    activeUsers={1}
                    maxUsers={maxUsers}
                    isTrialPeriod={isTrialPeriod}
                    pricePerExtraUser={pricePerExtraUser ?? 0}
                    onAddUsers={handleAddUsers}
                    onRemoveUsers={handleRemoveUsers}
                />
                <MandateCard
                    mandate={mandate}
                    isTerminationRequested={waitingRequestAction === "cancel-subscription"}
                    companyName={config.app.title}
                    testMode={config.app.mollie.testmode}
                    mollie={mollie}
                    mollieReady={ready}
                    onCardSubmit={handleCardSubmit}
                    onMandateSubmit={handleMandateSubmit}
                />
            </SimpleGrid>

            <InvoicesCard />
        </SeaShellBox>
    );
};